import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "./utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart, faBookmark, faComment, faCopy, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { copyPost } from "../../services/api";
import * as Sentry from "@sentry/react";
import { CommunityError } from "../../errors/sentryErrors";

// Field names mirror what /api/community/posts/mine returns. This component used
// to read post.trip.destination, post.likeCount and post.trip.itinerary — none of
// which that endpoint sends — so every card rendered "Trip to Unknown", a broken
// image and "Submitted on" with no date.
const PublishedPostCard = ({ post, onResubmitClick }) => {
  const {
    slug,
    title,
    destination,
    coverImage,
    status,
    stats = {},
    rejectionReason,
    rejectionNote,
    publishedAt,
    submittedAt,
  } = post;
  const { likes = 0, saves = 0, comments = 0, copies = 0 } = stats;
  const navigate = useNavigate();
  const [isCopying, setIsCopying] = useState(false);

  // The card was inert — nothing opened the post. A post that is not yet approved
  // 404s on the public route, so the owner needs ?preview=true, which the API
  // honours only for the post owner (see getPostBySlug.ownerPreviewAllowed).
  const openPost = () => {
    if (!slug) return;
    navigate(status === 'approved' ? `/community/${slug}` : `/community/${slug}?preview=true`);
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Do you want to copy this itinerary to your drafts?')) return;
    
    setIsCopying(true);
    try {
      const response = await copyPost(post.id);
      // You can only have one draft copy of a given post — a second click
      // opens the one you already made rather than creating another.
      toast.success(
        response.alreadyCopied
          ? "You've already copied this one — opening your draft."
          : 'Itinerary copied successfully!',
        { icon: '✨' },
      );
      // The copy endpoint returns { trip: { id, destination, status } } — there is
      // no top-level tripId, so this navigated to /itinerary/undefined and the page
      // then 404'd on GET /api/itinerary/trips/undefined.
      navigate(`/itinerary/${response.trip.id}`);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      if (err.status === 429) {
        toast.error(`Slow down. Try again in ${err.retryAfter || 'a few'} seconds.`);
      } else {
        toast.error('Failed to copy itinerary.');
      }
    } finally {
      setIsCopying(false);
    }
  };

  const renderStatusBadge = () => {
    switch (status) {
      case 'approved':
        return <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full border border-green-200">Live</span>;
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full border border-yellow-200">Pending Review</span>;
      case 'rejected':
        return <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full border border-red-200">Action Required</span>;
      case 'unpublished':
        return <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full border border-gray-200">Taken Down</span>;
      default:
        return null;
    }
  };

  return (
    <div
      onClick={openPost}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPost();
        }
      }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col h-full cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-500/30"
    >
      <div className="relative h-40 sm:h-48">
        {coverImage ? (
          <img
            src={coverImage}
            alt={destination || "Trip"}
            className="w-full h-full object-cover"
          />
        ) : (
          /* source.unsplash.com was retired and now fails to load, which is why
             every card showed a broken-image icon. A gradient beats a dead URL. */
          <div className="w-full h-full bg-gradient-to-br from-teal-500 to-teal-700" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {renderStatusBadge()}
        </div>

        <h3 className="absolute bottom-3 left-4 text-white font-semibold text-lg drop-shadow-md">
          {title || destination || "Untitled trip"}
        </h3>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center text-gray-500 text-sm mb-4">
          <FontAwesomeIcon icon={faCalendarAlt} className="w-4 h-4 mr-1.5" />
          <span>
            {status === 'approved' && publishedAt
              ? `Published ${formatDate(publishedAt)}`
              : submittedAt
                ? `Submitted ${formatDate(submittedAt)}`
                : 'Not submitted yet'}
          </span>
        </div>

        {status === 'approved' && (
          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-50">
            <div className="flex items-center gap-4 text-gray-500">
              <span className="flex items-center gap-1.5 text-sm" title="Likes">
                <FontAwesomeIcon icon={faHeart} className="w-4 h-4 text-pink-500" /> {likes}
              </span>
              <span className="flex items-center gap-1.5 text-sm" title="Saves">
                <FontAwesomeIcon icon={faBookmark} className="w-4 h-4 text-blue-500" /> {saves}
              </span>
              <span className="flex items-center gap-1.5 text-sm" title="Comments">
                <FontAwesomeIcon icon={faComment} className="w-4 h-4 text-indigo-500" /> {comments}
              </span>
              <button 
                onClick={handleCopy}
                disabled={isCopying}
                className={`flex items-center gap-1.5 text-sm transition-colors ${isCopying ? 'opacity-50 cursor-not-allowed' : 'hover:text-teal-600'}`} 
                title="Copy to Drafts"
              >
                <FontAwesomeIcon icon={faCopy} className={`w-4 h-4 text-teal-500 ${isCopying ? 'animate-pulse' : ''}`} /> {copies}
              </button>
            </div>
          </div>
        )}

        {status === 'rejected' && (
          <div className="mt-auto pt-3 border-t border-red-50">
            <p className="text-sm font-medium text-red-600 mb-1">Moderator Note:</p>
            <p className="text-sm text-gray-600 line-clamp-2 mb-3 bg-red-50 p-2 rounded border border-red-100">
              {rejectionNote || rejectionReason || "Does not meet community guidelines."}
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); onResubmitClick(post); }}
              className="w-full py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              Fix and Resubmit
            </button>
          </div>
        )}

        {status === 'unpublished' && (
          <div className="mt-auto pt-3 border-t border-gray-50">
             <p className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
              This post was taken down by moderators.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishedPostCard;
